/**
 * API client for backend communication
 * Handles authentication headers and request/response formatting
 * Automatically refreshes expired tokens
 */

import type { RefreshTokenResponse } from '@dovydaskukalis/shared/types/api/auth/responses';

import { env } from '@/config/env';
import { useWorkspaceStore } from '@/features/workspaces/store/workspace-store';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  requiresAuth?: boolean;
  workspaceScoped?: boolean; // Auto-prefix with /workspaces/:workspaceId from store (default: true)
  workspaceId?: string; // Override workspace ID (useful for workspace creation/switching)
  apiVersion?: string; // API version (default: 'v1', set to '' to skip)
  _isRetry?: boolean; // Internal flag to prevent infinite retry loops
}

export interface ApiError {
  message: string;
  statusCode: number;
}

/**
 * Refreshes the access token using refresh token
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  try {
    // Refresh endpoint is public
    const response = await fetch(`${env.apiUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const json = (await response.json()) as { success?: boolean; data?: RefreshTokenResponse };
    const newAccessToken = json.success && json.data ? json.data.accessToken : null;

    if (newAccessToken) {
      localStorage.setItem('accessToken', newAccessToken);
      return newAccessToken;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Makes authenticated API request to backend
 * Automatically refreshes expired tokens and retries
 * Automatically adds API version and workspaceId
 * @param endpoint - API endpoint path (e.g., '/projects', '/profiles/me')
 * @param options - Request options
 * @param options.apiVersion - API version (default: 'v1', set to '' to skip)
 * @param options.requiresAuth - Require authentication (default: true)
 * @param options.workspaceScoped - Auto-prefix with /workspaces/:workspaceId (default: true)
 * @param options.workspaceId - Override workspace ID from store (useful for switching workspaces)
 */
export async function apiClient<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    requiresAuth = true,
    workspaceScoped = true,
    workspaceId: overrideWorkspaceId,
    apiVersion = 'v1',
    _isRetry = false,
  } = options;

  // Get workspaceId from store or use override
  const workspaceId = overrideWorkspaceId ?? useWorkspaceStore.getState().workspace?.id;

  // Build the final endpoint with API version and workspace scope
  let finalEndpoint = endpoint;

  // Step 1: Add API version prefix if not already present and apiVersion is not empty
  if (apiVersion && !endpoint.startsWith('/api/')) {
    finalEndpoint = `/api/${apiVersion}${endpoint}`;
  }

  // Step 2: Inject workspace scope if needed
  if (workspaceScoped && workspaceId) {
    const apiVersionMatch = /^(\/api\/v\d+)(\/.*)/.exec(finalEndpoint);
    if (apiVersionMatch) {
      // Insert workspace after API version: /api/v1/profiles -> /api/v1/workspaces/:id/profiles
      finalEndpoint = `${apiVersionMatch[1]}/workspaces/${workspaceId}${apiVersionMatch[2]}`;
    } else {
      // No API version found, add workspace at start: /profiles -> /workspaces/:id/profiles
      finalEndpoint = `/workspaces/${workspaceId}${finalEndpoint}`;
    }
  }

  const url = `${env.apiUrl}${finalEndpoint}`;

  // Add auth header if required
  if (requiresAuth) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  // Add content type for requests with body
  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle 401 Unauthorized - attempt token refresh
  if (response.status === 401 && requiresAuth && !_isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry original request with new token
      return apiClient<T>(endpoint, { ...options, _isRetry: true });
    }

    // Refresh failed - clear auth data and redirect to login
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/auth/login';
    throw new Error('Session expired. Please log in again.');
  }

  // Handle non-OK responses
  if (!response.ok) {
    const error: ApiError = {
      message: 'Request failed',
      statusCode: response.status,
    };

    try {
      const errorData = (await response.json()) as { message?: string };
      error.message = errorData.message ?? error.message;
    } catch {
      // Fallback if response is not JSON
      error.message = response.statusText || error.message;
    }

    throw new Error(error.message);
  }

  // Handle empty responses (204, etc)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  // Parse JSON response
  const json = (await response.json()) as { success?: boolean; data?: T };

  // Backend wraps responses in { success: true, data: {...} }
  // Unwrap and return the data
  if (json.success && json.data !== undefined) {
    return json.data;
  }

  // If not wrapped, return as-is (for compatibility)
  return json as T;
}
